import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { buildIlikeOr, quotePostgrestValue } from "@/lib/postgrest";

export interface TeachingListFilters {
  search?: string;
  parashaId?: string;
  hebrewYear?: number;
  turno?: "manha" | "tarde";
  festivityId?: string;
  festivitySlug?: string;
  dateFrom?: string;
  dateTo?: string;
  gregYear?: number;
  gregMonth?: number;
  order?: "recent" | "oldest";
  limit?: number;
  offset?: number;
}

const filtersSchema = z.object({
  search: z.string().optional(),
  parashaId: z.string().uuid().optional(),
  hebrewYear: z.number().int().optional(),
  turno: z.enum(["manha", "tarde"]).optional(),
  festivityId: z.string().uuid().optional(),
  festivitySlug: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  gregYear: z.number().int().min(2000).max(2100).optional(),
  gregMonth: z.number().int().min(1).max(12).optional(),
  order: z.enum(["recent", "oldest"]).default("recent"),
  limit: z.number().int().min(1).max(100).default(24),
  offset: z.number().int().min(0).default(0),
});

/**
 * Padrões de título por festividade (tolerantes a acentos/caixa).
 * Aplicados via ilike sobre `title` e `custom_title`.
 */
export const FESTIVITY_TITLE_PATTERNS: Record<string, string[]> = {
  pessach: ["pessach", "pesach", "pêssach", "páscoa"],
  shavuot: ["shavuot", "chavuot", "pentecoste"],
  sukot: ["sukot", "sukkot", "sucot", "sucót", "tabernaculo", "tabernáculo"],
  "rosh-hashana": ["rosh hashan", "rosh ha-shan"],
  "yom-kipur": ["yom kipur", "yom kippur", "iom kipur"],
  chanuka: ["chanuk", "hanuk", "januc"],
  purim: ["purim"],
  "tu-bishvat": ["tu bishvat", "tu b'shvat", "tu bshvat", "tu-bishvat"],
  "shemini-atzeret": ["shemini atzeret", "sheminí atzéret", "shemini atzeres"],
  "simchat-torah": ["simchat torah", "simchat torá", "simjat torá", "simchat tora"],
};

function daysInMonth(y: number, m: number): number {
  return new Date(y, m, 0).getDate();
}

export const listTeachings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: TeachingListFilters) => filtersSchema.parse(data ?? {}))
  .handler(async ({ data, context }) => {
    let q = context.supabase
      .from("teachings")
      .select(
        "id, title, custom_title, thumbnail_url, teaching_date, turno, hebrew_year, hebrew_date, youtube_video_id, parasha_id, parashiot(name_translit, name_pt, number, slug), speakers(name), festivities(name_pt, slug)",
        { count: "exact" },
      )
      .order("teaching_date", { ascending: data.order === "oldest", nullsFirst: false })
      .order("turno", { ascending: true, nullsFirst: false })
      .range(data.offset, data.offset + data.limit - 1);

    if (data.search) {
      const clause = buildIlikeOr(["title", "custom_title"], data.search);
      if (clause) q = q.or(clause);
    }

    if (data.parashaId) q = q.eq("parasha_id", data.parashaId);
    if (data.hebrewYear) q = q.eq("hebrew_year", data.hebrewYear);
    if (data.turno) q = q.eq("turno", data.turno);
    if (data.festivityId) q = q.eq("festivity_id", data.festivityId);

    // Festividade por slug
    // - "__any__"  => qualquer festividade classificada
    // - "__none__" => sem festividade classificada
    // - slug real  => resolve id e filtra por festivity_id (com fallback para padrões de título)
    if (data.festivitySlug === "__any__") {
      q = q.not("festivity_id", "is", null);
    } else if (data.festivitySlug === "__none__") {
      q = q.is("festivity_id", null);
    } else if (data.festivitySlug) {
      const { data: fest } = await context.supabase
        .from("festivities")
        .select("id")
        .eq("slug", data.festivitySlug)
        .maybeSingle();
      const patterns = FESTIVITY_TITLE_PATTERNS[data.festivitySlug];
      const orParts: string[] = [];
      if (fest?.id) orParts.push(`festivity_id.eq.${fest.id}`);
      if (patterns && patterns.length > 0) {
        for (const p of patterns) {
          const pattern = quotePostgrestValue(`%${p}%`);
          orParts.push(`title.ilike.${pattern}`, `custom_title.ilike.${pattern}`);
        }
      }
      if (orParts.length > 0) q = q.or(orParts.join(","));
    }

    // Data explícita
    if (data.dateFrom) q = q.gte("teaching_date", data.dateFrom);
    if (data.dateTo) q = q.lte("teaching_date", data.dateTo);

    // Ano/mês gregoriano (combináveis com todos os filtros acima)
    if (data.gregYear && data.gregMonth) {
      const y = data.gregYear,
        m = data.gregMonth;
      const from = `${y}-${String(m).padStart(2, "0")}-01`;
      const to = `${y}-${String(m).padStart(2, "0")}-${String(daysInMonth(y, m)).padStart(2, "0")}`;
      q = q.gte("teaching_date", from).lte("teaching_date", to);
    } else if (data.gregYear) {
      q = q
        .gte("teaching_date", `${data.gregYear}-01-01`)
        .lte("teaching_date", `${data.gregYear}-12-31`);
    }
    // Nota: filtro por mês só é aplicado em conjunto com o ano gregoriano.

    const { data: rows, error, count } = await q;
    if (error) throw error;
    return { rows: rows ?? [], count: count ?? 0 };
  });

export const getTeaching = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("teachings")
      .select("*, parashiot(*), speakers(*), festivities(*)")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw error;
    if (!row) throw new Error("Ensino não encontrado");

    // ensinos relacionados da mesma parashá
    let related: any[] = [];
    if (row.parasha_id) {
      const { data: rel } = await context.supabase
        .from("teachings")
        .select("id, title, custom_title, thumbnail_url, teaching_date, turno, hebrew_year")
        .eq("parasha_id", row.parasha_id)
        .neq("id", row.id)
        .order("teaching_date", { ascending: false })
        .limit(6);
      related = rel ?? [];
    }
    return { teaching: row, related };
  });

export const listRecentTeachings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("teachings")
      .select(
        "id, title, custom_title, thumbnail_url, teaching_date, turno, hebrew_date, parashiot(name_translit, number)",
      )
      .order("teaching_date", { ascending: false, nullsFirst: false })
      .limit(8);
    if (error) throw error;
    return data ?? [];
  });

export const listFilterOptions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const [parashiot, speakers, festivities, years, boundaries] = await Promise.all([
      context.supabase
        .from("parashiot")
        .select("id, number, name_translit, name_pt, slug")
        .order("number"),
      context.supabase.from("speakers").select("id, name").order("name"),
      context.supabase.from("festivities").select("id, name_pt, slug").order("name_pt"),
      // Anos agregados no banco: evita ler todas as linhas de `teachings`
      // (duas varreduras completas) a cada abertura do catálogo.
      (context.supabase as any).rpc("teaching_filter_years").maybeSingle(),
      context.supabase
        .from("hebrew_year_boundaries")
        .select("hebrew_year, rosh_hashanah")
        .order("rosh_hashanah"),
    ]);
    const yearsRow = (years as any)?.data as {
      hebrew_years?: number[];
      greg_years?: number[];
    } | null;
    const hebrewYears = (yearsRow?.hebrew_years ?? []).filter((y) => typeof y === "number");
    const gregSet = new Set<number>(yearsRow?.greg_years ?? []);
    const bounds = (boundaries.data ?? []) as { hebrew_year: number; rosh_hashanah: string }[];
    // Para cada ano gregoriano, calcular anos hebraicos que abrange (Jan 1 e Dez 31).
    function hebrewYearFor(dateStr: string): number | null {
      let last: number | null = null;
      for (const b of bounds) {
        if (b.rosh_hashanah <= dateStr) last = b.hebrew_year;
        else break;
      }
      return last;
    }
    const gregYears = Array.from(gregSet)
      .sort((a, b) => b - a)
      .map((y) => {
        const h1 = hebrewYearFor(`${y}-01-01`);
        const h2 = hebrewYearFor(`${y}-12-31`);
        const hebrew = h1 && h2 ? (h1 === h2 ? String(h1) : `${h1}/${h2}`) : null;
        return { year: y, hebrew };
      });

    return {
      parashiot: parashiot.data ?? [],
      speakers: speakers.data ?? [],
      festivities: festivities.data ?? [],
      hebrewYears: hebrewYears,
      gregYears,
    };
  });

const updateSchema = z.object({
  id: z.string().uuid(),
  custom_title: z.string().trim().max(300).nullable().optional(),
  parasha_id: z.string().uuid().nullable().optional(),
  speaker_id: z.string().uuid().nullable().optional(),
  festivity_id: z.string().uuid().nullable().optional(),
  turno: z.enum(["manha", "tarde"]).nullable().optional(),
  teaching_date: z.string().nullable().optional(),
  hebrew_year: z.number().int().nullable().optional(),
  hebrew_date: z.string().nullable().optional(),
  torah_ref: z.string().nullable().optional(),
  haftarah_ref: z.string().nullable().optional(),
  brit_ref: z.string().nullable().optional(),
  psalm_ref: z.string().nullable().optional(),
  needs_review: z.boolean().optional(),
});

export const updateTeaching = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: z.infer<typeof updateSchema>) => updateSchema.parse(data))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Acesso negado");
    const { id, ...updates } = data;
    if ("custom_title" in updates) {
      const v = (updates.custom_title ?? "") as string | null;
      updates.custom_title = v && String(v).trim().length > 0 ? String(v).trim() : null;
    }
    const { error } = await context.supabase.from("teachings").update(updates).eq("id", id);
    if (error) throw error;
    return { ok: true };
  });
